import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, X } from "lucide-react";
import axios from "axios";
import { useCart } from "@/context/CartContext";
import { API, formatINR, SHOP } from "@/constants";

const EASE = [0.16, 1, 0.3, 1];

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const orderId = searchParams.get("order_id");
  const [state, setState] = useState({ status: "checking", order: null });
  const { clearCart } = useCart();

  useEffect(() => {
    if (orderId) {
      // Razorpay flow: payment already verified server-side before redirect
      (async () => {
        try {
          const { data } = await axios.get(`${API}/orders/${orderId}/status`);
          if (data.payment_status === "paid") {
            setState({ status: "paid", order: data });
            clearCart();
          } else if (data.payment_status === "awaiting_payment") {
            setState({ status: "upi", order: data });
            clearCart();
          } else {
            setState({ status: "failed", order: data });
          }
        } catch {
          setState({ status: "error", order: null });
        }
      })();
      return;
    }
    if (!sessionId) {
      setState({ status: "error", order: null });
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", order: data });
          clearCart();
          return;
        }
        if (["expired", "failed"].includes(data.payment_status) || data.status === "expired") {
          setState({ status: "failed", order: data });
          return;
        }
      } catch {
        // keep polling
      }
      attempts += 1;
      if (attempts < 24) timer = setTimeout(poll, 2500);
      else setState({ status: "timeout", order: null });
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clearCart]);

  return (
    <main data-testid="payment-success-page" className="flex min-h-screen items-center justify-center bg-forest px-6 text-cream">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: EASE }}
        className="w-full max-w-lg border border-cream/15 p-10 text-center md:p-14"
      >
        {state.status === "checking" && (
          <>
            <Loader2 size={40} className="mx-auto animate-spin text-marigold" />
            <h1 className="mt-8 font-serif text-3xl italic md:text-4xl" data-testid="payment-status-heading">
              Confirming your payment...
            </h1>
            <p className="mt-4 text-sm text-cream/60">This takes just a moment.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-marigold text-forest">
              <Check size={28} strokeWidth={3} />
            </span>
            <h1 className="mt-8 font-serif text-4xl italic md:text-5xl" data-testid="payment-status-heading">
              Dhanyabad! Order placed.
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-cream/70" data-testid="payment-success-detail">
              Payment of {formatINR(state.order?.total ?? 0)} received. Order{" "}
              <span className="text-marigold">#{state.order?.order_id?.slice(0, 8)}</span> is with our
              florists — we will call you shortly to confirm delivery.
            </p>
            <Link
              to="/"
              data-testid="payment-success-home-link"
              className="mt-10 inline-block rounded-full bg-cream px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-forest transition-colors duration-300 hover:bg-marigold"
            >
              Back to the Shop
            </Link>
          </>
        )}
        {state.status === "upi" && (
          <>
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-marigold text-forest">
              <Check size={28} strokeWidth={3} />
            </span>
            <h1 className="mt-8 font-serif text-4xl italic md:text-5xl" data-testid="payment-status-heading">
              Order placed! One step left.
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-cream/70" data-testid="upi-instructions">
              Pay {formatINR(state.order?.total ?? 0)} to our UPI ID{" "}
              <span className="rounded bg-cream/10 px-2 py-1 font-mono text-sm text-marigold" data-testid="upi-id-text">
                {SHOP.upiId}
              </span>{" "}
              ({SHOP.upiName}) using Paytm, GPay or PhonePe. Then send us the payment
              screenshot on WhatsApp — order{" "}
              <span className="text-marigold">#{state.order?.order_id?.slice(0, 8)}</span>.
            </p>
            <a
              href={`https://wa.me/${SHOP.whatsapp}?text=${encodeURIComponent(
                `Namaste Utsav Flowers! I placed order #${state.order?.order_id?.slice(0, 8)} of ${formatINR(state.order?.total ?? 0)} on your website. Sharing my UPI payment screenshot.`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="upi-whatsapp-button"
              className="mt-10 inline-block rounded-full bg-cream px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-forest transition-colors duration-300 hover:bg-marigold"
            >
              Send Screenshot on WhatsApp
            </a>
          </>
        )}
        {(state.status === "failed" || state.status === "timeout" || state.status === "error") && (
          <>
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-cream/30 text-cream">
              <X size={26} />
            </span>
            <h1 className="mt-8 font-serif text-3xl italic md:text-4xl" data-testid="payment-status-heading">
              {state.status === "timeout" ? "Still confirming..." : "Payment not completed"}
            </h1>
            <p className="mt-4 text-sm text-cream/60">
              {state.status === "timeout"
                ? "We are still waiting for the bank to confirm. Please WhatsApp us your name and we will verify."
                : "No money was taken. You can try again or order over WhatsApp."}
            </p>
            <Link
              to="/shop"
              data-testid="payment-retry-link"
              className="mt-10 inline-block rounded-full border border-cream/30 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-cream hover:text-forest"
            >
              Try Again
            </Link>
          </>
        )}
      </motion.div>
    </main>
  );
}
